/* KOYO-CUSTOM: ada-sort-select — WCAG 2.1.1 keyboard operability for Xtra's custom selects.
 *
 * Xtra converts native <select id> (sort_by, currency, product variant pickers, etc.) into a
 * decorative BVSelect widget (custom-async.js selectRun): it keeps the native <select> as a
 * transparent overlay but adds tabindex="-1", removing it from the keyboard tab order. The widget
 * (<a class="bv_atual"> + option <a> links) has no role/aria/keydown, so keyboard/SR users cannot
 * operate the control.
 *
 * Fix (non-destructive; the real native <select> stays the single keyboard control):
 *   1) remove tabindex="-1" so the native <select> is focusable;
 *   2) take the decorative widget's <a> out of the tab order (avoid duplicate tab stops);
 *   3) on native `change` (keyboard), sync the widget's displayed label + active option, because
 *      BVSelect only updates itself on decorative-link click (keyboard selection would otherwise
 *      leave the visible widget stale).
 *
 * IMPORTANT (reconcile, do not mark-and-skip): Xtra destroys/recreates select wrappers (e.g. the
 * address country->province rebuild) and rewrites tabindex on panel hide/show. So this is an
 * INVARIANT enforced continuously, not a one-shot init: the MutationObserver watches `tabindex`
 * (and `class`) and re-applies whenever Xtra re-adds tabindex="-1" or rebuilds a widget. reconcile()
 * is idempotent.
 *
 * The associated visible-focus fix (native select is transparent / off-screen on .f8sr) is CSS in
 * layout/theme.liquid (KOYO-CUSTOM: ada-select-focus).
 */
(function () {
  'use strict';

  function isInit(sel) {
    return sel.classList.contains('select-init') || sel.classList.contains('semantic-select-initialized');
  }

  // Mirror BVSelect (plugin-selects.js) after a keyboard change so the widget matches the native value:
  //   (a) the collapsed label `.bv_atual`, and (b) the highlighted dropdown row `.li.active`.
  // BVSelect only updates itself on a decorative option CLICK (not on native `change`, and not when the
  // dropdown re-opens), so both would otherwise go stale after keyboard selection. The row list prepends
  // a `.li.nofocus` search row (searchbox:true), so index against `.li:not(.nofocus)` — which stays 1:1
  // with the native options — never the raw `.li` list (that would be off by the search row).
  function syncWidget(sel) {
    try {
      var wrap = sel.closest('.select-wrapper');
      if (!wrap) return;
      var atual = wrap.querySelector('.bv_atual');
      var opt = sel.options[sel.selectedIndex];
      if (atual && opt) {
        // rebuild via DOM nodes (no innerHTML): <span class="sl_inner">TEXT</span> + existing arrow
        var arrow = atual.querySelector('.arrows_bv');
        var span = document.createElement('span');
        span.className = 'sl_inner';
        span.textContent = opt.textContent; // textContent => no XSS
        atual.textContent = '';
        atual.appendChild(span);
        if (arrow) { atual.appendChild(document.createTextNode(' ')); atual.appendChild(arrow); }
      }
      var ul = wrap.querySelector('.bv_ul_inner');
      if (ul) {
        // `.active` is the class BVSelect's own option-click handler toggles for the visible highlight
        // (plugin-selects.js; async-select.css .li.active). NOT `.bv_selected` — that is BVSelect's
        // initial/disabled-row state and is display:none inside the .f8sr sort dropdown (page-collection.css).
        var rows = ul.querySelectorAll('.li:not(.nofocus)'); // exclude BVSelect's prepended search row (1:1 with options)
        for (var i = 0; i < rows.length; i++) rows[i].classList.remove('active');
        if (rows[sel.selectedIndex]) rows[sel.selectedIndex].classList.add('active');
      }
    } catch (e) { /* never break the widget */ }
  }

  // Only pull a native select into the tab order if it is actually perceivable. Xtra keeps hidden
  // DUPLICATE selects (mobile menu, quick-view, the sticky add-to-cart bar .f8ps) with tabindex="-1"
  // on purpose; stripping it there would leak an invisible control into the tab order (WCAG 2.4.3).
  function isPerceivable(sel) {
    if (typeof sel.checkVisibility === 'function') {
      if (!sel.checkVisibility()) return false; // display:none / visibility:hidden / content-visibility
    } else if (sel.offsetParent === null) {
      return false; // fallback: display:none (opacity:0 overlay still has an offsetParent)
    }
    // opacity/transform-hidden bars (e.g. the sticky add-to-cart .f8ps at rest) keep pointer-events:none
    var host = sel.closest('.f8ps, [aria-hidden="true"]');
    if (host) {
      var cs = getComputedStyle(host);
      if (cs.pointerEvents === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
    }
    return true;
  }

  function reconcile(sel) {
    if (!sel || sel.tagName !== 'SELECT' || !isInit(sel)) return;
    // 1) keep the (overlaid) native select in the tab order — re-remove if Xtra re-added it — but only
    //    when the select is actually visible (don't tab into hidden duplicate/sticky selects)
    if (isPerceivable(sel)) {
      if (sel.getAttribute('tabindex') === '-1') sel.removeAttribute('tabindex');
    }
    // 2) keep the decorative widget out of the tab order
    var wrap = sel.closest('.select-wrapper') || sel.parentNode;
    if (wrap) {
      var deco = wrap.querySelectorAll('.bv_atual, .bv_ul_inner a, .bv_ul_inner button');
      for (var i = 0; i < deco.length; i++) {
        if (deco[i].getAttribute('tabindex') !== '-1') deco[i].setAttribute('tabindex', '-1');
      }
    }
    // 3) sync widget display on keyboard change (bind once per select element)
    if (!sel.dataset.koyoChangeBound) {
      sel.dataset.koyoChangeBound = '1';
      sel.addEventListener('change', function () { syncWidget(sel); });
    }
  }

  function sweep(root) {
    var nodes = (root || document).querySelectorAll('select.select-init, select.semantic-select-initialized');
    for (var i = 0; i < nodes.length; i++) reconcile(nodes[i]);
  }

  try {
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        if (m.type === 'attributes' && m.target && m.target.tagName === 'SELECT') reconcile(m.target);
        if (m.addedNodes) {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var n = m.addedNodes[j];
            if (n.nodeType !== 1) continue;
            if (n.tagName === 'SELECT') reconcile(n);
            if (n.querySelectorAll) sweep(n);
          }
        }
      }
    });
    mo.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'tabindex'] });
  } catch (e) { /* no-op */ }

  if (document.readyState !== 'loading') sweep(document);
  else document.addEventListener('DOMContentLoaded', function () { sweep(document); });
  window.addEventListener('load', function () { sweep(document); });
})();
